// v14at-p4 F4 — Globale store voor AutoMeasure background jobs.
//
// React Context + useReducer. Enige plek waar polling-timers leven.
// Mounts in App.jsx als `<MeasurementJobsProvider>`.
//
// Store-schema per job (zie f4_scope §3.2):
//   {
//     job_id, project_id, zone_id, address_canonical, country_code,
//     ui_state, backend_status, status_reason, progress_hint, retry_count,
//     result, error_code, error_title, error_message,
//     created_at, started_at, completed_at,
//     poll_interval_ms, poll_error_streak, last_polled_at, polling_started_at,
//     polling_health, flash_pending,
//     updated_at
//   }
//
// localStorage key: `emma.measurement_jobs.v1`. `result` wordt NIET
// gepersisteerd (wordt opnieuw opgehaald via GET bij hydrate).
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import {
  cancelMeasurementJob,
  createMeasurementJob,
  getMeasurementJob,
  isAsyncJobsEnabled,
} from "../lib/measurementJobsApi";
import {
  deriveUiState,
  derivePollingHealth,
  filterHydratedJobs,
  isSuperseded,
  nextPollInterval,
  shouldPoll,
  _constants,
} from "../lib/measurementJobsFsm";

const STORAGE_KEY = "emma.measurement_jobs.v1";

const initialState = { jobs: {} };

/**
 * v14at-p4 F4.4 (Defect F) — Synchrone lazy initializer die localStorage
 * direct bij useReducer-mount leest. Dit elimineert de race waarin een
 * mount-fase persist-effect `{"jobs":{}}` naar localStorage schrijft
 * vóór de hydrate-effect kan lezen.
 *
 * Terminal `failed` en `completed` jobs blijven behouden (zie
 * filterHydratedJobs). `polling_health` wordt gereset zodat een
 * hervatte sessie opnieuw een gezonde poll-cyclus start zonder de
 * backend-status aan te raken.
 */
function hydrateInitialState() {
  try {
    const raw = typeof window !== "undefined"
      ? window.localStorage.getItem(STORAGE_KEY)
      : null;
    if (!raw) return initialState;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return initialState;
    const kept = filterHydratedJobs(parsed.jobs || parsed || {});
    const resynced = {};
    for (const [jid, entry] of Object.entries(kept)) {
      resynced[jid] = {
        ...entry,
        polling_health: "ok",
        poll_error_streak: 0,
        last_polled_at: null,
        // `result`-blobs worden niet gepersisteerd; voor terminale
        // completed/reviewed jobs wordt de volledige payload opnieuw
        // via GET /jobs/{id} opgehaald zodra polling resumeert.
        result: null,
      };
    }
    return { jobs: resynced };
  } catch (_e) {
    return initialState;
  }
}

function reducer(state, action) {
  switch (action.type) {
    case "HYDRATE": {
      return { ...state, jobs: action.jobs || {} };
    }
    case "JOB_CREATED": {
      const { job } = action;
      return {
        ...state,
        jobs: { ...state.jobs, [job.job_id]: job },
      };
    }
    case "JOB_UPDATED": {
      const { jobId, patch } = action;
      const prev = state.jobs[jobId];
      if (!prev) return state;
      return {
        ...state,
        jobs: {
          ...state.jobs,
          [jobId]: { ...prev, ...patch, updated_at: new Date().toISOString() },
        },
      };
    }
    case "JOB_REMOVED": {
      const { jobId } = action;
      if (!state.jobs[jobId]) return state;
      const copy = { ...state.jobs };
      delete copy[jobId];
      return { ...state, jobs: copy };
    }
    case "FLASH_CONSUMED": {
      const { jobId } = action;
      const prev = state.jobs[jobId];
      if (!prev || !prev.flash_pending) return state;
      return {
        ...state,
        jobs: {
          ...state.jobs,
          [jobId]: { ...prev, flash_pending: false },
        },
      };
    }
    default:
      return state;
  }
}

const MeasurementJobsCtx = createContext(null);

export function MeasurementJobsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, hydrateInitialState);
  // Mutable ref op de laatste jobs-snapshot zodat de polling-loop geen stale
  // closures gebruikt.
  const jobsRef = useRef(state.jobs);
  jobsRef.current = state.jobs;

  // v14at-p4 F4.4 (Defect F) — Hydrate gebeurt nu synchroon in de
  // useReducer-initializer (hydrateInitialState), dus geen aparte
  // mount-effect meer nodig. Dit voorkomt de race met de persist-effect.

  // Persisteer bij elke jobs-mutatie (zonder `result`-blobs).
  useEffect(() => {
    try {
      const trimmed = {};
      for (const [jid, entry] of Object.entries(state.jobs)) {
        const { result, ...rest } = entry;
        trimmed[jid] = rest;
      }
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ jobs: trimmed }));
    } catch (e) {
      // Quota of security-restrictie → stil negeren.
    }
  }, [state.jobs]);

  // Polling-loop (gedeelde tick elke 500ms; per-job interval wordt bepaald
  // door `shouldPoll(...)`).
  useEffect(() => {
    if (!isAsyncJobsEnabled()) return undefined;
    const tickMs = 500;
    const interval = setInterval(async () => {
      const now = Date.now();
      const jobs = jobsRef.current;
      for (const [jobId, entry] of Object.entries(jobs)) {
        if (!entry.job_id) continue;
        if (
          !shouldPoll(entry.backend_status, now, entry.last_polled_at, entry.poll_interval_ms, {
            pollingStartedAt: entry.polling_started_at,
            pollingHealth: entry.polling_health,
          })
        ) {
          continue;
        }
        // Fire-and-forget: elke poll is onafhankelijk.
        pollOnce(jobId).catch(() => {});
      }
    }, tickMs);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollOnce = useCallback(async (jobId) => {
    const entry = jobsRef.current[jobId];
    if (!entry) return;
    try {
      const response = await getMeasurementJob(jobId);
      const nowIso = new Date().toISOString();
      const prevUi = entry.ui_state;
      const nextUi = deriveUiState(response, prevUi);
      const flashOnce = prevUi !== "completed" && nextUi === "completed";
      dispatch({
        type: "JOB_UPDATED",
        jobId,
        patch: {
          backend_status: response.status,
          status_reason: response.status_reason || entry.status_reason || null,
          progress_hint: response.progress_hint || null,
          retry_count: response.retry_count ?? entry.retry_count ?? 0,
          result: response.result || null,
          result_building_id: response.result_building_id || null,
          error_code: response.error_code || null,
          error_title: response.error_title || null,
          error_message: response.error_message || null,
          completed_at: response.completed_at || entry.completed_at || null,
          started_at: response.started_at || entry.started_at || null,
          poll_interval_ms: nextPollInterval(response.poll_interval_hint_ms, 0),
          poll_error_streak: 0,
          last_polled_at: Date.now(),
          polling_health: "ok",
          ui_state: nextUi,
          flash_pending: flashOnce ? true : entry.flash_pending,
          is_superseded: isSuperseded(response),
          updated_at: nowIso,
        },
      });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        // Job bestaat niet meer (bv. cross-tenant of hard revoked).
        dispatch({
          type: "JOB_UPDATED",
          jobId,
          patch: {
            ui_state: "cancelled",
            backend_status: "cancelled",
            status_reason: "not_found",
            polling_health: "ok",
            last_polled_at: Date.now(),
          },
        });
        return;
      }
      const nextStreak = (entry.poll_error_streak || 0) + 1;
      const nextHealth = derivePollingHealth("error", {
        errorStreak: entry.poll_error_streak || 0,
        pollingStartedAt: entry.polling_started_at,
      });
      dispatch({
        type: "JOB_UPDATED",
        jobId,
        patch: {
          poll_error_streak: nextStreak,
          poll_interval_ms: nextPollInterval(entry.poll_interval_ms, nextStreak),
          last_polled_at: Date.now(),
          polling_health: nextHealth,
        },
      });
    }
  }, []);

  const startJob = useCallback(
    async ({ address, projectId, zoneId, countryCode = "BE-VLG" }) => {
      const payload = {
        address,
        project_id: projectId || null,
        zone_id: zoneId || null,
        country_code: countryCode,
      };
      const response = await createMeasurementJob(payload);
      const nowIso = new Date().toISOString();
      const job = {
        job_id: response.job_id,
        project_id: projectId || null,
        zone_id: zoneId || null,
        address_canonical: address,
        country_code: countryCode,
        ui_state: deriveUiState({ status: response.status }, "creating"),
        backend_status: response.status,
        status_reason: null,
        progress_hint: null,
        retry_count: 0,
        result: null,
        result_building_id: null,
        error_code: null,
        error_title: null,
        error_message: null,
        created_at: response.created_at || nowIso,
        completed_at: null,
        started_at: null,
        poll_interval_ms: _constants.DEFAULT_POLL_MS,
        poll_error_streak: 0,
        last_polled_at: null,
        polling_started_at: Date.now(),
        polling_health: "ok",
        flash_pending: false,
        is_superseded: false,
        deduplicated: !!response.deduplicated,
        updated_at: nowIso,
      };
      dispatch({ type: "JOB_CREATED", job });
      return job;
    },
    []
  );

  const cancelJob = useCallback(async (jobId) => {
    try {
      await cancelMeasurementJob(jobId);
    } catch (e) {
      // 409 = already terminal; 404 = al verdwenen → beide OK voor dismissal.
    }
    dispatch({
      type: "JOB_UPDATED",
      jobId,
      patch: {
        ui_state: "cancelled",
        backend_status: "cancelled",
        status_reason: "user_cancelled",
        polling_health: "ok",
      },
    });
  }, []);

  const markReviewed = useCallback((jobId) => {
    dispatch({ type: "JOB_UPDATED", jobId, patch: { ui_state: "reviewed" } });
  }, []);

  const markApplying = useCallback((jobId) => {
    dispatch({ type: "JOB_UPDATED", jobId, patch: { ui_state: "applying" } });
  }, []);

  const markApplied = useCallback((jobId) => {
    dispatch({
      type: "JOB_UPDATED",
      jobId,
      patch: { ui_state: "applied", completed_at: new Date().toISOString() },
    });
  }, []);

  const dismissJob = useCallback((jobId) => {
    dispatch({ type: "JOB_REMOVED", jobId });
  }, []);

  const consumeFlash = useCallback((jobId) => {
    dispatch({ type: "FLASH_CONSUMED", jobId });
  }, []);

  const findJobByZone = useCallback(
    (projectId, zoneId) => {
      // Zoek de meest-recente niet-dismissed job voor (project, zone).
      const candidates = Object.values(state.jobs).filter(
        (j) => j.project_id === projectId && j.zone_id === zoneId && j.ui_state !== "applied"
      );
      candidates.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      return candidates[0] || null;
    },
    [state.jobs]
  );

  const value = useMemo(
    () => ({
      jobs: state.jobs,
      startJob,
      cancelJob,
      pollOnce,
      markReviewed,
      markApplying,
      markApplied,
      dismissJob,
      consumeFlash,
      findJobByZone,
      asyncEnabled: isAsyncJobsEnabled(),
    }),
    [
      state.jobs,
      startJob,
      cancelJob,
      pollOnce,
      markReviewed,
      markApplying,
      markApplied,
      dismissJob,
      consumeFlash,
      findJobByZone,
    ]
  );

  return (
    <MeasurementJobsCtx.Provider value={value}>{children}</MeasurementJobsCtx.Provider>
  );
}

export function useMeasurementJobs() {
  const ctx = useContext(MeasurementJobsCtx);
  if (!ctx) {
    // Zonder provider: lever een no-op fallback zodat componenten in
    // sync-flow geen crash veroorzaken.
    return {
      jobs: {},
      startJob: async () => {
        throw new Error("MeasurementJobsProvider niet gemount");
      },
      cancelJob: async () => {},
      pollOnce: async () => {},
      markReviewed: () => {},
      markApplying: () => {},
      markApplied: () => {},
      dismissJob: () => {},
      consumeFlash: () => {},
      findJobByZone: () => null,
      asyncEnabled: false,
    };
  }
  return ctx;
}

export function useJob(jobId) {
  const { jobs } = useMeasurementJobs();
  return jobId ? jobs[jobId] || null : null;
}
